import {
  useContext,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  type RefObject,
  type CSSProperties,
  type ReactNode,
  type MouseEventHandler,
} from 'react'
import { createPortal } from 'react-dom'
import { ModalPortalContext } from './Modal'

type Align = 'start' | 'end'
type Placement = 'top' | 'bottom'

interface Position {
  top: number
  left: number
  placement: Placement
}

interface UseFloatingMenuOptions {
  open: boolean
  onClose: () => void
  anchorRef: RefObject<HTMLElement>
  floatingRef: RefObject<HTMLElement>
  width: number
  align?: Align
  gap?: number
  closeOnEscape?: boolean
}

const VIEWPORT_MARGIN = 8

/**
 * Shared positioning engine for anchored floating menus/popovers. Measures the
 * anchor with getBoundingClientRect, aligns the floating element to one edge,
 * flips above the anchor when there isn't room below, and keeps everything in
 * sync on scroll/resize. Also wires up outside-click and Escape to close.
 */
export function useFloatingMenu({
  open,
  onClose,
  anchorRef,
  floatingRef,
  width,
  align = 'end',
  gap = 4,
  closeOnEscape = true,
}: UseFloatingMenuOptions): Position | null {
  const [position, setPosition] = useState<Position | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }

    const update = () => {
      const anchor = anchorRef.current
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()

      let left = align === 'end' ? rect.right - width : rect.left
      left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN),
      )

      const floatingHeight = floatingRef.current?.offsetHeight ?? 0
      const spaceBelow = window.innerHeight - rect.bottom
      const placement: Placement =
        floatingHeight > 0 && spaceBelow < floatingHeight + gap ? 'top' : 'bottom'

      const top = placement === 'bottom' ? rect.bottom + gap : rect.top - gap
      setPosition({ top, left, placement })
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, width, align, gap, anchorRef, floatingRef])

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (anchorRef.current?.contains(target)) return
      if (floatingRef.current?.contains(target)) return
      onClose()
    }
    const handleKey = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') onClose()
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open, onClose, closeOnEscape, anchorRef, floatingRef])

  return position
}

interface FloatingMenuProps {
  /** Whether the menu is open. When false, nothing is rendered. */
  open: boolean
  /** Called when the menu should close (outside click or Escape). */
  onClose: () => void
  /** The element the menu is positioned against. */
  anchorRef: RefObject<HTMLElement>
  /** Width of the floating element in px, used for horizontal positioning. */
  width: number
  /** Align the floating element to the anchor's start (left) or end (right) edge. */
  align?: Align
  /** Vertical gap between the anchor and the floating element. */
  gap?: number
  /** Whether pressing Escape closes the menu. Defaults to true. */
  closeOnEscape?: boolean
  className?: string
  style?: CSSProperties
  onClick?: MouseEventHandler<HTMLDivElement>
  children: ReactNode
}

/**
 * Portals an anchored floating menu/popover to document.body and positions it
 * relative to `anchorRef` using {@link useFloatingMenu}. Handles flipping near
 * the viewport edge, repositioning on scroll/resize, and closing on outside
 * click or Escape. The floating element is hidden until its position has been
 * measured to avoid a first-paint flash in the wrong spot.
 */
export default function FloatingMenu({
  open,
  onClose,
  anchorRef,
  width,
  align = 'end',
  gap = 4,
  closeOnEscape = true,
  className,
  style,
  onClick,
  children,
}: FloatingMenuProps) {
  const modalPortal = useContext(ModalPortalContext)
  const floatingRef = useRef<HTMLDivElement>(null)
  const position = useFloatingMenu({
    open,
    onClose,
    anchorRef,
    floatingRef,
    width,
    align,
    gap,
    closeOnEscape,
  })

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      ref={floatingRef}
      onClick={onClick}
      className={className}
      style={{
        position: 'fixed',
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        zIndex: 9999,
        visibility: position ? 'visible' : 'hidden',
        transform: position?.placement === 'top' ? 'translateY(-100%)' : undefined,
        ...style,
      }}
    >
      {children}
    </div>,
    modalPortal ?? document.body,
  )
}
